import { useEffect, useState } from "react";

import { onSessionEnd } from "@utils/sessionTeardown";

const STYLE_SELECTOR = 'style, link[rel="stylesheet"]';

/** Source stylesheet node in this document → its copy in the popup. */
type StyleCopies = Map<Node, Element>;

const copyStyleNode = (node: Node, to: Document, copies: StyleCopies) => {
  const copy = to.importNode(node, true) as Element;
  to.head.appendChild(copy);
  copies.set(node, copy);
};

const copyStyles = (from: Document, to: Document, copies: StyleCopies) => {
  from
    .querySelectorAll(STYLE_SELECTOR)
    .forEach((node) => copyStyleNode(node, to, copies));
  // Theme (`dark`) and base classes live on <html>/<body>.
  to.documentElement.className = from.documentElement.className;
  to.body.className = from.body.className;
  // The panel fills the window.
  to.documentElement.style.height = "100%";
  to.body.style.height = "100%";
  to.body.style.margin = "0";
};

type Popout = { win: Window; container: HTMLElement; styleCopies: StyleCopies };

/**
 * Opens a separate browser window that this tab renders into via a portal
 * (MDRS-486): a blank same-origin popup, so it behaves the same in every
 * browser and can be maximised or moved to another screen. Deliberately not
 * Document Picture-in-Picture — that is Chromium-only, always on top of the
 * app, and its position can't be set.
 *
 * The window has no URL of its own and holds no app instance — it shows only
 * what this tab portals into it, using this tab's session and cache. So it is
 * closed whenever that content must go: on logout (session teardown), when
 * this tab goes away, and when the owning component unmounts.
 */
export const usePopoutWindow = (title: string) => {
  const [popout, setPopout] = useState<Popout | null>(null);

  const openPopout = ({ width, height }: { width: number; height: number }) => {
    // `_blank`, not a fixed name: a shared name would let a second app tab
    // take over (and later close) this tab's window.
    const win = window.open("", "_blank", `popup,width=${width},height=${height}`);
    // Blocked popup — stay in the app.
    if (!win) return false;

    const doc = win.document;
    doc.title = title;
    const styleCopies: StyleCopies = new Map();
    copyStyles(document, doc, styleCopies);
    const container = doc.createElement("div");
    container.className = "h-full";
    doc.body.appendChild(container);
    setPopout({ win, container, styleCopies });
    return true;
  };

  useEffect(() => {
    if (!popout) return;
    const { win, styleCopies } = popout;

    // The user closed the window themselves.
    const handleClosed = () => setPopout(null);
    const closeWindow = () => win.close();

    // Keep the copied stylesheets in step with this document's head: lazy
    // chunks add CSS after the window opened, and Radix's scroll lock adds and
    // removes a <style> per dialog — unmirrored removals would pile up in a
    // long-lived popup.
    const styleObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes, removedNodes }) => {
        addedNodes.forEach((node) => {
          if (node instanceof Element && node.matches(STYLE_SELECTOR)) {
            copyStyleNode(node, win.document, styleCopies);
          }
        });
        removedNodes.forEach((node) => {
          styleCopies.get(node)?.remove();
          styleCopies.delete(node);
        });
      });
    });
    styleObserver.observe(document.head, { childList: true });

    win.addEventListener("pagehide", handleClosed);
    window.addEventListener("pagehide", closeWindow);
    const unsubscribe = onSessionEnd(closeWindow);

    return () => {
      // Detach first: closing from here is ours, not the user's.
      win.removeEventListener("pagehide", handleClosed);
      window.removeEventListener("pagehide", closeWindow);
      unsubscribe();
      styleObserver.disconnect();
      win.close();
    };
  }, [popout]);

  return {
    popoutContainer: popout?.container ?? null,
    openPopout,
    closePopout: () => setPopout(null),
  };
};
